// noinspection JSUnresolvedReference

import React, {useContext, useEffect, useId, useMemo, useRef, useState} from "react";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import {ErpCalculationApi, ProjectPermission} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import moment from "moment-timezone";
import {Chart} from "chart.js";
import {getContrastBackgroundColors, getContrastBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {chartColors} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import ReportingInfoTooltip from "../../../../../components/ReportingInfoTooltip.jsx";
import {useLegacyPeriodReportError} from "../../Layout/LegacyPeriodReportBoundary.jsx";
import toast from "react-hot-toast";
import {SensitiveMetric, useHasPermission, useHasSensitiveMetric} from "../../../../../components/auth/rolesAndPermissions.js";
import {useRenderCurrencyValue, useRenderNumber} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import {nullableMoneyMapToMajorUnitValues} from "../../../../../utils/reportingMetricRatios.js";
import ReportingSnapshotStatus from "../../../../admin/ReportingSnapshotStatus.jsx";
import {isSnapshotPeriodIncomplete, snapshotDateValue, snapshotPeriodValue, withSnapshotMissingDays} from "../../../../../utils/reportingSnapshotAvailability.js";

function requestedDates(since, until) {
    const dates = [];
    const cursor = since.clone();
    const end = until.format('YYYY-MM-DD');
    while (cursor.format('YYYY-MM-DD') <= end) {
        dates.push(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'day');
    }
    return dates;
}

function snapshotGranularMap(values, snapshot, dates) {
    if (!isSnapshotPeriodIncomplete(snapshot)) {
        return values ?? {};
    }

    return Object.fromEntries(dates.map((date) => [
        date,
        snapshotDateValue(values?.[date] ?? 0, snapshot, date),
    ]));
}

export function totalOrderStatusCount(selectedData, snapshot) {
    const count = (selectedData?.orderAmountCompleted ?? 0)
        + (selectedData?.orderAmountReceived ?? 0)
        + (selectedData?.orderAmountCancelled ?? 0);

    return snapshotPeriodValue(count, snapshot);
}

export default function OrderPerformancePanel({project}) {

    let {t} = useTranslation();
    let {et} = useLegacyPeriodReportError();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {customerWholesaleRequestFilter} = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();
    let renderCurrencyValue = useRenderCurrencyValue();
    let renderNumber = useRenderNumber();

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, textColor} = chartColors;

    let [overviewData, setOverviewData] = useState(null);
    let [overviewLoading, setOverviewLoading] = useState(true);

    let hasSensitiveMetric = useHasSensitiveMetric();
    let hasPermission = useHasPermission();

    let [userSettings] = useUserSettings();

    const snapshot = overviewData?.snapshot;
    const snapshotIncomplete = isSnapshotPeriodIncomplete(snapshot);
    const snapshotDates = useMemo(() => snapshotIncomplete
        ? withSnapshotMissingDays(snapshot, requestedDates(since, until))
        : [], [snapshot, snapshotIncomplete, since, until]);

    let selectedData = useMemo(() => {

        if(userSettings === null) { return {} };

        return {
            orderTurnover: snapshotPeriodValue(userSettings?.isMarketing ? overviewData?.orderTurnoverGross : overviewData?.orderTurnoverNet, snapshot),
            orderTurnoverGranular: snapshotGranularMap(userSettings?.isMarketing ? overviewData?.orderTurnoverGrossGranular : overviewData?.orderTurnoverNetGranular, snapshot, snapshotDates),
            orderProfit: snapshotPeriodValue(userSettings?.isMarketing ? overviewData?.orderProfitGross : overviewData?.orderProfitNet, snapshot),
            orderProfitGranular: snapshotGranularMap(userSettings?.isMarketing ? overviewData?.orderProfitGrossGranular : overviewData?.orderProfitNetGranular, snapshot, snapshotDates),
            orderAmount: snapshotPeriodValue(userSettings?.isMarketing ? overviewData?.orderAmountGross : overviewData?.orderAmountNet, snapshot),
            orderAmountGranular: snapshotGranularMap(userSettings?.isMarketing ? overviewData?.orderAmountGrossGranular : overviewData?.orderAmountNetGranular, snapshot, snapshotDates),
            orderAverage: snapshotPeriodValue(userSettings?.isMarketing ? overviewData?.orderAverageGross : overviewData?.orderAverageNet, snapshot),
            orderAverageGranular: snapshotGranularMap(userSettings?.isMarketing ? overviewData?.orderAverageGrossGranular : overviewData?.orderAverageNetGranular, snapshot, snapshotDates),
            orderAmountCompleted: snapshotPeriodValue(overviewData?.orderAmountCompleted, snapshot),
            orderAmountReceived: snapshotPeriodValue(overviewData?.orderAmountReceived, snapshot),
            orderAmountCancelled: snapshotPeriodValue(overviewData?.orderAmountCancelled, snapshot),
            orderAmountCompletedGranular: snapshotGranularMap(overviewData?.orderAmountCompletedGranular, snapshot, snapshotDates),
            orderAmountReceivedGranular: snapshotGranularMap(overviewData?.orderAmountReceivedGranular, snapshot, snapshotDates),
            orderAmountCancelledGranular: snapshotGranularMap(overviewData?.orderAmountCancelledGranular, snapshot, snapshotDates),
        };
    }, [overviewData, userSettings, snapshot, snapshotDates]);

    const isMarketingView = userSettings?.isMarketing === true;
    const orderPopulationScope = isMarketingView
        ? t('reporting_lab.metric_basis.marketing.short_label', {defaultValue: 'Bez storen'})
        : t('reporting_lab.products.completed_orders_basis', {defaultValue: 'Dokončené objednávky'});
    const orderPopulationDescription = isMarketingView
        ? `${t('reporting_lab.metric_basis.marketing.label', {defaultValue: 'Marketingový výkon'})}: ${t('reporting_lab.metric_basis.marketing.description', {defaultValue: 'Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu.'})}`
        : `${t('reporting_lab.metric_basis.business.label', {defaultValue: 'Obchodní výkon'})}: ${t('reporting_lab.metric_basis.business.description', {defaultValue: 'Zahrnuje pouze dokončené objednávky.'})}`;

    useEffect(() => {
        if (!project) {
            return;
        }

        let active = true;
        setOverviewLoading(true);
        setOverviewData(null);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformance({
                projectId: project.id,
                erpFilter: {
                    ...customerWholesaleRequestFilter,
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            })
            .then((nextOverviewData) => {
                if (!active) {
                    return;
                }

                setOverviewData(nextOverviewData);
                setOverviewLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };

    }, [project?.id, since, until, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter]);

    const chartOptions = {
        maintainAspectRatio: false,
        animation: {
            duration: 0
        },
        scales: {
            y: {
                border: {
                    display: false,
                },
                ticks: {
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    color: darkMode ? gridColor.dark : gridColor.light,
                },
            },
            x: {
                border: {
                    display: false,
                },
                ticks: {
                    padding: 10,
                    align: 'center',
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    };

    let granularTurnoverId = useId();
    const granularTurnoverCanvas = useRef(null);

    let granularProfitId = useId();
    const granularProfitCanvas = useRef(null);

    let granularOrderAmountId = useId();
    const granularOrderAmountCanvas = useRef(null);

    let granularAverageTurnoverId = useId();
    const granularAverageTurnoverCanvas = useRef(null);

    useEffect(() => {
        if (overviewLoading || overviewData === null || userSettings === null) {
            return;
        }

        let dateKeys = Object.keys(
            selectedData?.orderTurnoverGranular ?? []
        );

        let labels = dateKeys.map((dateString) => moment(dateString).format('DD.MM.'));

        let createdCharts = [];

        let createChart = (canvasRef, configuration) => {
            if (canvasRef.current === null) {
                return null;
            }

            let chart = new Chart(canvasRef.current, configuration);
            createdCharts.push(chart);

            return chart;
        };

        // -- -- -------------------------------------------------------------------------------------------------------

        // -- -- -------------------------------------------------------------------------------------------------------

        let turnoverChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('reporting_lab.legacy_order_metrics.turnover_label', {defaultValue: 'Obrat objednávek'}),
                    backgroundColor: getContrastBackgroundColors()[1],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[1],
                    data: nullableMoneyMapToMajorUnitValues(selectedData?.orderTurnoverGranular),
                }
            ],
        };

        createChart(granularTurnoverCanvas, {
            type: 'bar',
            data: turnoverChartData,
            options: chartOptions
        });
        // -- -- -------------------------------------------------------------------------------------------------------

        let profitChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('reporting_lab.table.contribution', {defaultValue: 'Hrubý zisk po reklamě'}),
                    backgroundColor: getContrastBackgroundColors()[6],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[6],
                    data: nullableMoneyMapToMajorUnitValues(selectedData?.orderProfitGranular),
                }
            ],
        };

        createChart(granularProfitCanvas, {
            type: 'bar',
            data: profitChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------

        let orderAmountGranular = isMarketingView
            ? dateKeys.map((dateKey) => {
                const completed = selectedData?.orderAmountCompletedGranular?.[dateKey];
                const received = selectedData?.orderAmountReceivedGranular?.[dateKey];
                return completed == null || received == null ? null : completed + received;
            })
            : Object.values(selectedData?.orderAmountGranular ?? []);

        let granularOrderAmountChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.amount'),
                    backgroundColor: getContrastBackgroundColors()[3],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[3],
                    data: orderAmountGranular,
                }
            ],
        };

        createChart(granularOrderAmountCanvas, {
            type: 'bar',
            data: granularOrderAmountChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------

        let granularAverageTurnoverChartData = {
            labels: labels,
            datasets: [
                {
                    label: t('orders.average-order-price'),
                    backgroundColor: getContrastBackgroundColors()[10],
                    hoverBackgroundColor: getContrastBackgroundHoverColors()[10],
                    data: nullableMoneyMapToMajorUnitValues(selectedData?.orderAverageGranular),
                }
            ]
        };

        createChart(granularAverageTurnoverCanvas, {
            type: 'line',
            data: granularAverageTurnoverChartData,
            options: chartOptions
        });

        // -- -- -------------------------------------------------------------------------------------------------------
        return () => {
            createdCharts.forEach((chart) => chart.destroy());
        };

    }, [isMarketingView, overviewData, overviewLoading, selectedData, userSettings]);

    let result = [];

    if (snapshotIncomplete) {
        result.push(<div key="order-snapshot-status" className="col-span-12"><ReportingSnapshotStatus snapshots={snapshot}/></div>);
    }

    result.push(<LoadingComponent
        key={'pp-turnover'}
        loading={overviewLoading || !overviewData}
        loadedClassName={"flex flex-col h-full"}
        className="comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700"
    >
        <header
            className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <div className="min-w-0">
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('reporting_lab.legacy_order_metrics.turnover_label', {defaultValue: 'Obrat objednávek'})}
                </h3>
                <p className="mt-1 text-[10px] leading-4 text-slate-500 dark:text-slate-400">{orderPopulationScope}</p>
            </div>
            <ReportingInfoTooltip position="right">
                <div className="text-xs text-gray-200">
                    {t('reporting_lab.legacy_order_metrics.turnover_help', {defaultValue: 'Částka odpovídá objednávkám ve zvolené populaci. Pole Net/Gross v tomto starším výpočtu rozlišují objednávkové stavy, nikoli cenu s DPH a bez DPH. Populace: {{scope}}.', scope: orderPopulationDescription})}
                </div>
            </ReportingInfoTooltip>
        </header>
        <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
            <span
                className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
            >
                {renderCurrencyValue(selectedData?.orderTurnover, true)}
            </span>
        </div>
        <div className={'p-2 mt-auto'}>
            <div className="grow">
                <canvas ref={granularTurnoverCanvas} id={granularTurnoverId}></canvas>
            </div>
        </div>
    </LoadingComponent>);

    if (hasSensitiveMetric(SensitiveMetric.Profit, project)) {
        result.push(<LoadingComponent
            key={'pp-profit'}
            loading={overviewLoading || !overviewData}
            loadedClassName={"flex flex-col h-full"}
            className="comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700"
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <div className="min-w-0">
                    <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                        {t('reporting_lab.table.contribution', {defaultValue: 'Hrubý zisk po reklamě'})}
                    </h3>
                    <p className="mt-1 text-[10px] leading-4 text-slate-500 dark:text-slate-400">{orderPopulationScope}</p>
                </div>
                <ReportingInfoTooltip position="right">
                    <div className="text-xs text-gray-200">
                        {t('reporting_lab.legacy_order_metrics.profit_help', {defaultValue: 'Hrubý zisk po reklamě odpovídá obratu objednávek po odečtení nákladů na prodané zboží a reklamních médií. Populace: {{scope}}.', scope: orderPopulationDescription})}
                    </div>
                </ReportingInfoTooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
                <span
                    className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                >
                    {renderCurrencyValue(selectedData?.orderProfit, true)}
                </span>
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    <canvas ref={granularProfitCanvas} id={granularProfitId}></canvas>
                </div>
            </div>
        </LoadingComponent>);
    }

    result.push(<LoadingComponent
        key={'pp-total'}
        loading={overviewLoading || !overviewData}
        loadedClassName={"flex flex-col h-full"}
        className="comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700"
    >
        <header
            className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
            <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                {isMarketingView ? t('orders.orders') : t('orders.completed-orders')}
            </h3>
            <ReportingInfoTooltip position="bottom">
                <div className="text-xs text-gray-200">
                    {t('tooltip.order.total')}
                </div>
            </ReportingInfoTooltip>
        </header>
        <div className="p-5 flex flex-col justify-center">
            <div className="text-center flex items-center justify-center gap-4 grow-1 shrink-1">
                <span
                    className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                >
                        {renderNumber(totalOrderStatusCount(selectedData, snapshot))}
                </span>
            </div>
            {isMarketingView && (
                <div className="grid grid-cols-3 gap-3 text-center mt-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            {t('orders.received')}
                        </div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {renderNumber(selectedData?.orderAmountReceived)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            {t('orders.completed')}
                        </div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {renderNumber(selectedData?.orderAmountCompleted)}
                        </div>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                            {t('orders.cancelled')}
                        </div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {renderNumber(selectedData?.orderAmountCancelled)}
                        </div>
                    </div>
                </div>
            )}
        </div>
        <div className={'p-2 mt-auto'}>
            <div className="grow">
                <canvas ref={granularOrderAmountCanvas} id={granularOrderAmountId}></canvas>
            </div>
        </div>
    </LoadingComponent>);

    // If we have offline spends, we do not show the average order price on this overview...
    if (hasPermission(ProjectPermission.ProjectOfflineSpends, project) === false) {
        result.push(<LoadingComponent
            key={'pp-average'}
            loading={overviewLoading || !overviewData}
            loadedClassName={"flex flex-col h-full"}
            className="comp__roundedBox reporting-surface rounded-lg flex flex-col justify-between col-span-12 sm:col-span-4 bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700"

        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('orders.average-order-price')}
                </h3>
                <ReportingInfoTooltip position="left">
                    <div className="text-xs text-gray-200">
                        {t('tooltip.order.average-price')}
                    </div>
                </ReportingInfoTooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-4 grow-1 shrink-1">
                <span
                    className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                >
                    {renderCurrencyValue(selectedData?.orderAverage, true)}
                </span>
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    <canvas ref={granularAverageTurnoverCanvas} id={granularAverageTurnoverId}></canvas>
                </div>
            </div>
        </LoadingComponent>);
    }

    return result;
}
